import { api } from '../utils/api';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface DailySales {
  date: string;
  totalOrders: number;
  totalRevenue: number;
  avgTicket: number;
}

export interface SalesReport {
  from: string;
  to: string;
  totalOrders: number;
  totalRevenue: number;
  avgTicket: number;
  daily: DailySales[];
}

export interface TopProduct {
  id: number;
  name: string;
  totalQty: number;
  totalRevenue: number;
}

export interface TopProductsReport {
  from: string;
  to: string;
  products: TopProduct[];
}

export interface HourlySlot {
  hour: number;
  label: string;
  totalOrders: number;
  totalRevenue: number;
}

export interface HourlyReport {
  date: string;
  hours: HourlySlot[];
}

export interface PrepAreaStat {
  area: number;
  areaLabel: string;
  totalOrders: number;
  avgMinutes: number;
  minMinutes: number;
  maxMinutes: number;
}

export interface PreparationTimeReport {
  from: string;
  to: string;
  areas: PrepAreaStat[];
}

// ── API calls ──────────────────────────────────────────────────────────────────

export async function getSalesReport(from: string, to: string): Promise<SalesReport> {
  const { data } = await api.get<SalesReport>('/reports/sales', { params: { from, to } });
  return data;
}

export async function getTopProducts(
  from: string,
  to: string,
  limit = 10,
): Promise<TopProductsReport> {
  const { data } = await api.get<TopProductsReport>('/reports/top-products', {
    params: { from, to, limit },
  });
  return data;
}

export async function getHourlyReport(date: string): Promise<HourlyReport> {
  const { data } = await api.get<HourlyReport>('/reports/hourly', { params: { date } });
  return data;
}

export function getExportUrl(from: string, to: string): string {
  // Build URL with token for direct download
  const base = (import.meta as any).env?.VITE_API_URL ?? '';
  return `${base}/api/reports/export?from=${from}&to=${to}`;
}

export async function getPreparationTimeReport(from: string, to: string): Promise<PreparationTimeReport> {
  const { data } = await api.get<PreparationTimeReport>('/reports/preparation-time', { params: { from, to } });
  return data;
}

export async function downloadCsvExport(from: string, to: string): Promise<void> {
  const response = await api.get('/reports/export', {
    params: { from, to },
    responseType: 'blob',
  });
  const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `ventas_${from}_${to}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
